import { FetchResult } from "@apollo/client"
import { Box, Button, Checkbox, Stack, Text, VStack } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { Fragment, MutableRefObject, useRef } from "react"
import {
  HomeRegistrationFragment,
  SoftArchiveRegistrationMutation,
  SoftArchiveRegistrationMutationHookResult,
  useHomeRegistrationsQuery
} from "../../api/generated"
import Pathicon from "../../pathicon/Pathicon"
import Tip from "../../tips/Tip"
import { useHomeRegistrationVars } from "../HomeRegistrationQueryVars"
import useSoftArchiveRegistration from "../useSoftArchiveRegistration"

type CheckableRegistrationIds = Record<number, boolean>

type MutationsRef = Record<
  number, // registrationId
  SoftArchiveRegistrationMutationHookResult[0]
>

// Since we need to call this hook for a list of registrations, we must
// use a "render" approach rather than attempt to call the hook in a list
// since in edge cases one of the registrations in the list could become
// unmounted (say, if a user was to set aside an included registration from
// by some other means, like a control menu). This could lead to a change in
// the number of hooks called from one render to the next.
function useSoftArchiveRegistrations(
  mutationsRef: MutableRefObject<MutationsRef>
) {
  return async function (registrationIds: CheckableRegistrationIds) {
    const promises = Object.entries(registrationIds).reduce<
      Promise<FetchResult<SoftArchiveRegistrationMutation>>[]
    >((acc, [registratinId, archive]) => {
      if (archive) {
        acc.push(mutationsRef.current[parseInt(registratinId)]())
      }
      return acc
    }, [])

    await Promise.all(promises)
  }
}

type UseSoftArchiveRegistrationProps = {
  registration: HomeRegistrationFragment
  mutationsRef: MutableRefObject<MutationsRef>
}

// Using component to register a mutation.
function UseSoftArchiveRegistration({
  registration,
  mutationsRef
}: UseSoftArchiveRegistrationProps) {
  const [mutate] = useSoftArchiveRegistration(registration)
  mutationsRef.current[registration.id] = mutate
  return null
}

function useInactiveRegistrations(registrations: HomeRegistrationFragment[]) {
  // Query for the registration IDs of the user's inactive registrations.
  const inactiveRegistrationsQuery = useHomeRegistrationsQuery({
    variables: {
      ...useHomeRegistrationVars(),
      // Not seen for 60 days.
      lastSeen: {
        lt: new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 60)
      }
    },
    // Ignoring inactive registrations when user doesn't have at least 7 registrations.
    skip: registrations.length <= 7
  })

  // Assemble the inactive registrations based on those that are found in the list
  // of provided registrations. This can result in further filtering out inactive
  // registrations in some cases, like when user is registered in Source Cohort
  // but that cohort has been removed from the main registrations list.
  const inactiveRegistrations = (
    inactiveRegistrationsQuery.data?.me?.registrations?.edges?.map(
      (edge) => edge?.node!
    ) || []
  )
    // Filter the registrations list by those registration IDs.
    .filter((inactiveRegistration) =>
      registrations.some(
        (registration) => inactiveRegistration.id === registration.id
      )
    )

  return inactiveRegistrations
}

function HomeSetAsidePathsTip({
  inactiveRegistrations
}: {
  inactiveRegistrations: HomeRegistrationFragment[]
}) {
  const mutationsRef = useRef<MutationsRef>({})
  const handleSoftArchiveRegistrations =
    useSoftArchiveRegistrations(mutationsRef)

  return (
    <Tip
      tip={{
        id: "home-set-aside-paths",
        title: `Clean up your Home Screen`,
        content: (
          <Formik
            initialValues={{} as CheckableRegistrationIds}
            onSubmit={async (values, form) => {
              await handleSoftArchiveRegistrations(values)
              // Clear out form.
              form.resetForm()
            }}
          >
            {(form) => {
              const checkedCount = Object.values(form.values).filter(
                Boolean
              ).length

              const isAllChecked = checkedCount === inactiveRegistrations.length
              const isIndeterminate = !!checkedCount && !isAllChecked

              return (
                <Form>
                  <VStack>
                    <Text color="inherit" mt={0}>
                      You have {inactiveRegistrations.length} Path(s) you
                      haven’t been active in for the last 60 days:
                    </Text>
                    <Box w="100%">
                      <Checkbox
                        isChecked={isAllChecked}
                        isIndeterminate={isIndeterminate}
                        onChange={(e) =>
                          form.setValues(
                            inactiveRegistrations.reduce<CheckableRegistrationIds>(
                              (acc, r) => {
                                acc[r.id] = e.target.checked
                                return acc
                              },
                              {}
                            )
                          )
                        }
                      >
                        Set <strong>all</strong> {inactiveRegistrations.length}{" "}
                        Path(s) aside?
                      </Checkbox>
                      <Stack pl={6} mt={1} spacing={1}>
                        {inactiveRegistrations.map((registration) => (
                          <Fragment key={registration.id}>
                            <Field
                              as={Checkbox}
                              name={registration.id}
                              isChecked={!!form.values[registration.id]}
                            >
                              {registration.group?.is_source_cohort ? (
                                <strong>{registration.resource?.name}</strong>
                              ) : (
                                <>
                                  <strong>{registration.resource?.name}</strong>
                                  : {registration.group?.name}
                                </>
                              )}
                            </Field>
                            {/* Register the soft archive mutation for this registration. */}
                            <UseSoftArchiveRegistration
                              registration={registration}
                              mutationsRef={mutationsRef}
                            />
                          </Fragment>
                        ))}
                      </Stack>
                    </Box>
                    <Button
                      type="submit"
                      colorScheme="whiteAlpha"
                      isLoading={form.isSubmitting}
                      isDisabled={!checkedCount}
                      cursor="pointer"
                      leftIcon={
                        <Pathicon
                          icon="sidebar-bottom"
                          transform="rotate(-90deg)"
                        />
                      }
                    >
                      Set aside {checkedCount} Path(s)
                    </Button>
                  </VStack>
                </Form>
              )
            }}
          </Formik>
        )
      }}
      icon={
        <Pathicon
          icon="sidebar-bottom"
          transform="rotate(-90deg)"
          size="1.4em"
        />
      }
      layout="dismissable"
      colorScheme="dark"
    />
  )
}

export function HomeSetAsidePathsTipContainer({
  registrations
}: {
  registrations: HomeRegistrationFragment[]
}) {
  const inactiveRegistrations = useInactiveRegistrations(registrations)
  return inactiveRegistrations.length ? (
    <HomeSetAsidePathsTip inactiveRegistrations={inactiveRegistrations} />
  ) : null
}

export default HomeSetAsidePathsTip
